import { ChangeDetectorRef, Component, inject, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { QuizHeaderComponent } from '../../../ui/views/quiz-header/quiz-header.component';
import { CardComponent } from '../../../ui/card/card.component';
import { ButtonComponent } from '../../../ui/button/button.component';
import { LoaderComponent } from '../../../ui/loader/loader.component';
import { TestService } from '../../../../core/services/test.service';
import {
  StudentTestService,
  TestScoreResultDto,
  QuestionDetailDto,
  SessionFullDto,
} from '../../../../core/services/student-test.service';
import { SwalService } from '../../../../core/services/utils/swal.service';
import { SessionResultMapperService } from '../../../../core/services/utils/session-result-mapper.service';
import { Test, TestQuestion } from '../../../../core/interfaces/test';
import { AnswerBank } from '../../../../core/interfaces/answer-bank';
import { PieComponent } from '../../../../shared/charts/pie/pie.component';
import { DurationPipe } from '../../../../shared/pipes/duration.pipe';
import { MathTextComponent } from '../../../../shared/math/math-text.component';

/**
 * Ce que l'élève peut faire de ce test une fois sa copie rendue.
 *
 * <p>Trois états, et non deux : entre « il peut recommencer » et « il ne le
 * peut plus », il y a « on ne sait pas » — la réponse n'est pas encore arrivée,
 * ou la requête a échoué. Ce troisième état n'est pas un détail : c'est lui qui
 * interdit de promettre quoi que ce soit par défaut.</p>
 */
export type EtatReprise = 'inconnu' | 'possible' | 'epuise';

/**
 * Déduit l'état de la reprise de la liste des tests encore ouverts à l'élève.
 *
 * <p>Le serveur applique déjà la règle complète en construisant cette liste —
 * tentatives restantes, fenêtre de passation, statut du test. Un test qui en
 * est absent ne peut plus être repassé ; un test qui y figure porte le nombre de
 * tentatives qu'il reste.</p>
 *
 * <p>Surtout ne pas recalculer ce nombre à partir du numéro de tentative de la
 * session : une session abandonnée garde son numéro sans avoir consommé de
 * tentative, et le compte serait faux.</p>
 */
export function etatReprise(
  disponibles: Pick<Test, 'id' | 'studentSummary'>[] | null | undefined,
  testId: number | null | undefined,
): { etat: EtatReprise; restantes: number | null } {
  if (!disponibles || testId == null) return { etat: 'inconnu', restantes: null };

  const ouvert = disponibles.find((t) => t.id === testId);
  if (!ouvert) return { etat: 'epuise', restantes: 0 };

  return { etat: 'possible', restantes: ouvert.studentSummary?.remainingAttempts ?? null };
}

/**
 * Clé du message affiché sous une copie qui n'atteint pas le seuil.
 *
 * <p>Le message était fixe et promettait une nouvelle tentative à tout le monde,
 * alors que chaque test diagnostique n'en autorise qu'une : l'élève lisait
 * « vous pouvez recommencer » au-dessus d'un bouton absent (rapport du
 * 04/09/2026, §8.4). La promesse n'est désormais faite que lorsqu'elle est
 * tenue, et la fin des tentatives s'annonce comme une étape franchie, pas comme
 * un refus.</p>
 */
export function cleMessageEchec(etat: EtatReprise, restantes: number | null): string {
  if (etat === 'epuise') return 'TESTS.FAILED_FINAL';
  if (etat === 'possible') {
    return restantes != null && restantes > 0
      ? 'TESTS.FAILED_RETRY_REMAINING'
      : 'TESTS.FAILED_RETRY_OPEN';
  }
  return 'TESTS.FAILED_MESSAGE';
}

// Per-answer display truth (from backend when available, local fallback otherwise)
interface AnswerDisplayDetail {
  isCorrect: boolean;
  wasSelected: boolean;
}

interface QuestionResult {
  question: TestQuestion;
  userAnswers: number[];
  isCorrect: boolean;
  isTimedOut: boolean;
  points: number;
  earnedPoints: number;
  timeSpent: number;
  // Keyed by answerId — backend truth for highlighting
  answerDetails: Map<number, AnswerDisplayDetail>;
}

interface TestResult {
  test: Test;
  totalQuestions: number;
  correctAnswers: number;
  wrongAnswers: number;
  unansweredQuestions: number;
  timedOutQuestions: number;
  score: number;
  percentage: number;
  passed: boolean;
  timeSpent: number;
  answers: Map<number, number[]>;
  timedOutQuestionIndices: Set<number>;
  autoSubmitted: boolean;
  questionResults: QuestionResult[];
}

interface ChartData {
  x: string;
  y: number;
  text: string;
  fill: string;
}

@Component({
  selector: 'app-result',
  standalone: true,
  imports: [MathTextComponent, 
    CommonModule,
    TranslateModule,
    QuizHeaderComponent,
    CardComponent,
    PieComponent,
    ButtonComponent,
    LoaderComponent,
    DurationPipe,
  ],
  templateUrl: './result.component.html',
  styleUrl: './result.component.css',
  // Le pipe sert aussi depuis la classe, pour composer le libellé de durée.
  providers: [DurationPipe],
})
export class ResultComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private testService = inject(TestService);
  private studentTestService = inject(StudentTestService);
  private mapper = inject(SessionResultMapperService);
  private durationPipe = inject(DurationPipe);
  private translate = inject(TranslateService);
  private swal = inject(SwalService);
  private destroy$ = new Subject<void>();

  questionTimes = new Map<number, number>();
  testId: number | null = null;
  sessionId: number | null = null;
  isLoading = true;
  hasError = false;
  errorMessage = '';
  isLTR = false;

  testResult: TestResult | null = null;
  sessionFull: SessionFullDto | null = null;
  chartData: ChartData[] = [];
  showDetailedResults = false;
  expandedQuestionIndex: number | null = null;
  totalPoints: number = 0;

  /**
   * Barème du test : la note se lit sur ce total. 20 sauf réglage contraire.
   *
   * <p>Distinct de {@link totalPoints}, qui est la somme des points des
   * questions. Les deux ne coïncident pas et n'ont pas à le faire : un test de
   * 18 questions à 1 point vaut 18 points bruts et se rend sur 20.</p>
   */
  gradingScale = 20;

  /**
   * Note ramenée sur le barème.
   *
   * <p>Dérivée du pourcentage, comme côté serveur : c'est lui qui est stocké
   * sur la session et relu à chaque enrichissement de l'écran. En repartir
   * garantit que la note ne contredit jamais le pourcentage affiché juste à
   * côté.</p>
   */
  get scaledScore(): number {
    const percentage = this.testResult?.percentage ?? 0;
    return Math.round((percentage / 100) * this.gradingScale * 100) / 100;
  }

  behavior!: {
    tabSwitchCount: number;
    focusLossCount: number;
    fullscreenExitCount: number;
  };

  /**
   * Ce que l'élève peut faire de ce test : le repasser, ou non.
   *
   * <p>Le bouton était affiché sans condition : il menait à l'ouverture d'une
   * session, que le serveur refusait dès que la fenêtre de passation était
   * fermée ou les tentatives épuisées. On demande donc au serveur ce qu'il
   * répondrait — le test figure-t-il encore parmi ceux que cet élève peut
   * prendre — plutôt que de rejouer sa règle ici, où elle finirait par
   * diverger.</p>
   *
   * <p>Tant que la réponse n'est pas là, l'état reste « inconnu » : ni bouton,
   * ni promesse de reprise.</p>
   */
  reprise: EtatReprise = 'inconnu';

  /** Tentatives qu'il reste, comptées par le serveur ; `null` si illimitées ou inconnues. */
  tentativesRestantes: number | null = null;

  /** Le bouton « Repasser » ne s'affiche que si le serveur l'accepterait. */
  get canRetake(): boolean {
    return this.reprise === 'possible';
  }

  /** Clé du message d'échec, accordée à ce que l'élève peut réellement faire. */
  get messageEchec(): string {
    return cleMessageEchec(this.reprise, this.tentativesRestantes);
  }

  ngOnInit(): void {
    this.isLTR = this.translate.currentLang !== 'ar';
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        this.isLTR = event.lang !== 'ar';
        this.updateChartData();
      });

    this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      // Le paramètre de route est l'identifiant de **session**, pas celui du
      // test : une copie se désigne par la tentative qui l'a produite, sinon
      // deux passages du même test mènent à la même adresse.
      this.sessionId = +params['id'] || null;

      if (this.sessionId) {
        this.behavior = { tabSwitchCount: 0, focusLossCount: 0, fullscreenExitCount: 0 };
        this.loadTestResults();
      } else {
        this.handleError(new Error('Invalid session ID'));
      }
    });
  }

  /**
   * Reconstruit l'état de la copie depuis le serveur.
   *
   * <p>L'écran a d'abord été écrit pour l'instant qui suit la remise : tout lui
   * arrivait dans l'état de navigation. Rouvert depuis l'historique — ou
   * simplement rechargé — il ne trouvait plus rien et annonçait à l'élève de
   * repasser le test. La correction est pourtant relisible : c'est elle qu'on
   * redemande ici.</p>
   */
  private async stateFromServer(sessionId: number): Promise<Record<string, unknown>> {
    const scoreRes = await firstValueFrom(
      this.studentTestService.getSessionScore(sessionId),
    );

    const scoreResult = scoreRes?.data;
    if (!scoreResult) throw new Error('Session score not found');

    // Les réponses de l'élève se relisent dans le détail par question : le
    // serveur y marque, option par option, celles qui ont été cochées.
    const answers: [number, number[]][] = (scoreResult.questionDetails ?? []).map(
      (d) => [
        d.questionId,
        d.selectedAnswerIds?.length
          ? d.selectedAnswerIds
          : (d.allAnswers ?? [])
              .filter((a) => a.wasSelected)
              .map((a) => a.id ?? a.answerId!),
      ],
    );

    return {
      scoreResult,
      sessionId,
      answers,
      timeSpent: scoreResult.timeSpentSeconds ?? 0,
      autoSubmitted: false,
      timedOutQuestions: [],
    };
  }

  private async loadTestResults(): Promise<void> {
    try {
      this.isLoading = true;
      this.hasError = false;

      if (!this.sessionId) throw new Error('Session ID is required');

      const navigation = this.router.getCurrentNavigation();
      let state = navigation?.extras?.state;
      if (!state || !state['answers']) state = window.history.state;

      // Pas d'état de navigation : on vient de l'historique ou d'un
      // rechargement. La copie se relit alors depuis le serveur, au lieu de
      // renvoyer l'élève repasser un test qu'il a déjà rendu.
      if (!state || !state['answers']) {
        state = await this.stateFromServer(this.sessionId);
      }

      // Extract scoreResult early — used by both calculateQuestionResults and mapResultData
      const scoreResult = state['scoreResult'] as TestScoreResultDto | undefined;

      this.testId = state['test']?.id ?? scoreResult?.testId ?? null;
      if (!this.testId) throw new Error('Test ID is required');

      let test: Test = state['test'];
      if (!test?.testQuestions?.length) {
        const testResponse = await firstValueFrom(
          this.testService.getStudentTestWithQuestions(this.testId),
        );
        if (testResponse.status !== 'SUCCESS' || !testResponse.data) {
          throw new Error('Failed to load test details');
        }
        test = testResponse.data;
      }

      if (!test.testQuestions || test.testQuestions.length === 0) {
        throw new Error('Test has no questions');
      }

      // En parallèle du reste : l'affichage des résultats n'attend pas de
      // savoir si le test est repassable, seul le bouton en dépend.
      this.checkRetakeAllowed(test);

      // Build answers map
      let answersMap: Map<number, number[]>;
      if (state['answers'] instanceof Map) {
        answersMap = state['answers'];
      } else if (Array.isArray(state['answers'])) {
        answersMap = new Map<number, number[]>(state['answers']);
      } else if (typeof state['answers'] === 'object') {
        answersMap = new Map<number, number[]>(
          Object.entries(state['answers']).map(([key, value]) => [
            Number(key),
            value as number[],
          ]),
        );
      } else {
        throw new Error('Invalid answers format');
      }

      const timedOutQuestionIndices = new Set<number>(state['timedOutQuestions'] || []);
      const autoSubmitted = state['autoSubmitted'] || false;

      this.behavior = state['behavior'] || { tabSwitchCount: 0, focusLossCount: 0, fullscreenExitCount: 0 };

      if (state['questionTimes']) {
        this.questionTimes = new Map<number, number>(state['questionTimes']);
      }

      // ✅ Pass backend questionDetails so per-question truth comes from server
      const questionResults = this.calculateQuestionResults(
        test.testQuestions,
        answersMap,
        timedOutQuestionIndices,
        scoreResult?.questionDetails,
      );

      // Le barème du serveur d'abord — c'est celui sur lequel la note a été
      // calculée ; le total du test, puis la somme des questions, ne servent
      // que de replis.
      this.totalPoints =
        scoreResult?.totalPoints ||
        test.totalPoints ||
        questionResults.reduce((sum, q) => sum + q.points, 0);

      // Le barème vient du test, pas du nombre de questions : c'est un
      // réglage, pas une conséquence de la composition.
      this.gradingScale = scoreResult?.gradingScale || test.gradingScale || 20;

      this.testResult = this.mapResultData(
        scoreResult,
        test,
        questionResults,
        answersMap,
        timedOutQuestionIndices,
        autoSubmitted,
        state['timeSpent'] || 0,
      );

      this.updateChartData();

      // ── Enrich from server session — authoritative source for all display values ──
      if (this.sessionId) {
        try {
          const sessionRes = await firstValueFrom(
            this.studentTestService.getSessionById(this.sessionId),
          );
          if (sessionRes?.data) {
            this.sessionFull = sessionRes.data;
            const s = this.sessionFull;

            console.log('SESSION DEBUG:', s);

            this.testResult = {
              ...this.testResult!,
              correctAnswers: s.correctAnswers  ?? this.testResult!.correctAnswers,
              wrongAnswers:   s.wrongAnswers    ?? this.testResult!.wrongAnswers,
              totalQuestions: s.totalQuestions  ?? this.testResult!.totalQuestions,
              score:          s.earnedPoints    ?? s.score ?? this.testResult!.score,
              percentage:     s.percentageScore ?? s.percentage ?? this.testResult!.percentage,
              timeSpent:      s.timeSpentSeconds ?? this.testResult!.timeSpent,
            };

            if (s.totalPoints != null && s.totalPoints > 0) {
              this.totalPoints = s.totalPoints;
            }

            console.log('chartData:', this.testResult);
            this.updateChartData();
            this.cdr.detectChanges();
          }
        } catch (err) {
          console.warn('Could not fetch full session data:', err);
        }
      }

      this.isLoading = false;

      console.log('✅ Test results finalized:', {
        correct: this.testResult.correctAnswers,
        wrong: this.testResult.wrongAnswers,
        unanswered: this.testResult.unansweredQuestions,
        timedOut: this.testResult.timedOutQuestions,
        percentage: this.testResult.percentage.toFixed(2),
        passed: this.testResult.passed,
        totalPoints: this.totalPoints,
        score: this.testResult.score,
        timeSpent: this.testResult.timeSpent,
        hasQuestionDetails: !!scoreResult?.questionDetails?.length,
      });

      if (autoSubmitted) {
        await this.swal.info(
          this.translate.instant('TESTS.AUTO_SUBMITTED_MESSAGE'),
          this.translate.instant('TESTS.AUTO_SUBMITTED'),
        );
      }
    } catch (error) {
      console.error('❌ Error loading test results:', error);
      this.handleError(error);
    }
  }

  /**
   * Builds per-question results.
   *
   * When backend provides `questionDetails`, those values are used directly
   * (isCorrect, earnedPoints, per-answer wasSelected/isCorrect). This is the
   * authoritative path because the student endpoint deliberately hides
   * answerBanks.isCorrect and questionBank.points during the exam.
   *
   * Falls back to local computation when no backend details are present
   * (e.g. direct URL navigation to the result page).
   */
  private calculateQuestionResults(
    questions: TestQuestion[],
    userAnswers: Map<number, number[]>,
    timedOutIndices: Set<number>,
    questionDetails?: QuestionDetailDto[],
  ): QuestionResult[] {
    // Build a lookup map for O(1) access
    const detailsMap = new Map<number, QuestionDetailDto>();
    questionDetails?.forEach((d) => detailsMap.set(d.questionId, d));

    return questions.map((testQuestion, index) => {
      const questionBank = testQuestion.question;
      if (!questionBank) {
        throw new Error(`Question bank data missing for question at index ${index}`);
      }

      const questionId = questionBank.id;
      const isTimedOut = timedOutIndices.has(index);
      const detail = detailsMap.get(questionId);

      // ── Correctness & points ──────────────────────────────────────────────
      let isCorrect: boolean;
      let earnedPoints: number;
      let points: number;

      if (detail) {
        // Backend truth — never recompute when the server has already graded
        isCorrect = detail.isCorrect;
        earnedPoints = detail.earnedPoints;
        points = detail.points || questionBank.points || 0;
      } else {
        // Local fallback (only when backend data is absent)
        points = questionBank.points || 0;
        const userAnswerIds = userAnswers.get(questionId) || [];
        const correctIds = (questionBank.answerBanks || [])
          .filter((a) => a.isCorrect)
          .map((a) => a.id);
        isCorrect =
          !isTimedOut &&
          this.areAnswersCorrect(userAnswerIds, correctIds, questionBank.questionType);
        earnedPoints = isCorrect ? points : 0;
      }

      // ── Per-answer display details ─────────────────────────────────────────
      const answerDetails = new Map<number, AnswerDisplayDetail>();

      if (detail?.allAnswers?.length) {
        // Backend provides exact wasSelected + isCorrect per answer.
        // La clé est `id` : c'est ainsi que l'API nomme l'identifiant d'une
        // option. Le lire sous `answerId` remplissait la carte de clés
        // `undefined`, et aucune réponse ne se surlignait.
        detail.allAnswers.forEach((a) =>
          answerDetails.set(a.id ?? a.answerId!, {
            isCorrect: a.isCorrect,
            wasSelected: a.wasSelected,
          }),
        );
      } else {
        // Fallback: derive from local answersMap + answerBanks.isCorrect
        const userAnswerIds = userAnswers.get(questionId) || [];
        (questionBank.answerBanks || []).forEach((a) =>
          answerDetails.set(a.id, {
            isCorrect: a.isCorrect,
            wasSelected: userAnswerIds.includes(a.id),
          }),
        );
      }

      return {
        question: testQuestion,
        userAnswers: userAnswers.get(questionId) || [],
        isCorrect,
        isTimedOut,
        points,
        earnedPoints,
        timeSpent: this.questionTimes.get(questionId) || 0,
        answerDetails,
      };
    });
  }

  private areAnswersCorrect(
    userAnswerIds: number[],
    correctAnswerIds: number[],
    questionType: string,
  ): boolean {
    if (userAnswerIds.length === 0) return false;

    const isMultipleChoice =
      questionType?.toLowerCase().includes('multiple') ||
      questionType?.toLowerCase().includes('checkbox');

    if (isMultipleChoice) {
      return (
        userAnswerIds.length === correctAnswerIds.length &&
        userAnswerIds.every((id) => correctAnswerIds.includes(id))
      );
    }
    return userAnswerIds.length === 1 && correctAnswerIds.includes(userAnswerIds[0]);
  }

  private mapResultData(
    scoreResult: TestScoreResultDto | undefined,
    test: Test,
    questionResults: QuestionResult[],
    answersMap: Map<number, number[]>,
    timedOutQuestionIndices: Set<number>,
    autoSubmitted: boolean,
    timeSpent: number,
  ): TestResult {
    const timedOutCount = timedOutQuestionIndices.size;

    let totalQuestions: number;
    let correctAnswers: number;
    let wrongAnswers: number;
    let unansweredQuestions: number;
    let percentage: number;
    let passed: boolean;
    let score: number;

    if (scoreResult) {
      totalQuestions = scoreResult.totalQuestions;
      correctAnswers = scoreResult.correctAnswers;
      wrongAnswers = scoreResult.wrongAnswers;
      percentage = scoreResult.percentageScore ?? scoreResult.percentage ?? 0;
      passed = scoreResult.passed;
      // `earnedPoints` est le nom que porte la charge utile ; `score` n'y
      // figure pas et laissait la note à `undefined`.
      score = scoreResult.earnedPoints ?? scoreResult.score ?? 0;
      unansweredQuestions =
        scoreResult.unansweredQuestions ??
        Math.max(0, totalQuestions - correctAnswers - wrongAnswers - timedOutCount);
    } else {
      totalQuestions = test.testQuestions?.length ?? questionResults.length;
      correctAnswers = questionResults.filter((q) => q.isCorrect).length;
      wrongAnswers = questionResults.filter(
        (q) => !q.isCorrect && !q.isTimedOut && q.userAnswers.length > 0,
      ).length;
      const earnedPoints = questionResults.reduce((sum, q) => sum + q.earnedPoints, 0);
      percentage = this.totalPoints > 0 ? (earnedPoints / this.totalPoints) * 100 : 0;
      const passingPercentage = test.passingPercentage ?? 50;
      passed = percentage >= passingPercentage;
      score = earnedPoints;
      unansweredQuestions = Math.max(0, totalQuestions - answersMap.size - timedOutCount);
    }

    return {
      test,
      totalQuestions,
      correctAnswers,
      wrongAnswers,
      unansweredQuestions,
      timedOutQuestions: timedOutCount,
      score,
      percentage,
      passed,
      timeSpent,
      answers: answersMap,
      timedOutQuestionIndices,
      autoSubmitted,
      questionResults,
    };
  }

  private updateChartData(): void {
    if (!this.testResult) return;

    this.translate
      .get([
        'CHART.CORRECT_ANSWERS',
        'CHART.WRONG_ANSWERS',
        'CHART.TIMED_OUT',
        'CHART.UNANSWERED',
      ])
      .subscribe((t) => {
        const { correctAnswers, wrongAnswers, timedOutQuestions, unansweredQuestions } =
          this.testResult!;

        this.chartData = this.mapper.buildPieChartData([
          { count: correctAnswers,    label: t['CHART.CORRECT_ANSWERS'], fill: '#14D520' },
          { count: wrongAnswers,      label: t['CHART.WRONG_ANSWERS'],   fill: '#d50b3e' },
          { count: timedOutQuestions, label: t['CHART.TIMED_OUT'],       fill: '#ff9800' },
          { count: unansweredQuestions, label: t['CHART.UNANSWERED'],    fill: '#9e9e9e' },
        ]);
      });
  }

  // ── Answer display helpers (use backend answerDetails as truth) ────────────

  /** Returns the highlight class for an answer item. */
  getAnswerClass(answerId: number, result: QuestionResult): string {
    const d = result.answerDetails.get(answerId);
    if (!d) return '';

    if (d.isCorrect && d.wasSelected)  return 'bg-success-subtle border-success border-2';
    if (d.isCorrect && !d.wasSelected) return 'bg-success-subtle border-success';
    if (!d.isCorrect && d.wasSelected) return 'bg-danger-subtle border-danger border-2';
    return '';
  }

  /** Whether the student selected this answer. */
  isAnswerSelected(answerId: number, result: QuestionResult): boolean {
    return result.answerDetails.get(answerId)?.wasSelected ?? false;
  }

  /** Whether this answer is correct (backend truth when available). */
  isAnswerCorrect(answerId: number, result: QuestionResult): boolean {
    return result.answerDetails.get(answerId)?.isCorrect ?? false;
  }

  // ── Question-level display helpers ────────────────────────────────────────

  getQuestionIcon(result: QuestionResult): string {
    if (result.isTimedOut)            return 'fas fa-clock text-warning';
    if (result.isCorrect)             return 'fas fa-check-circle text-success';
    if (result.userAnswers.length === 0) return 'fas fa-circle text-muted';
    return 'fas fa-times-circle text-danger';
  }

  getQuestionBadgeClass(result: QuestionResult): string {
    if (result.isTimedOut)            return 'bg-warning';
    if (result.isCorrect)             return 'bg-success';
    if (result.userAnswers.length === 0) return 'bg-secondary';
    return 'bg-danger';
  }

  // ── Score display ─────────────────────────────────────────────────────────

  getScoreColor(): string {
    return this.mapper.getScoreColorClass(this.testResult?.percentage);
  }

  // ── Utilities ─────────────────────────────────────────────────────────────

  getAnswerLabel(index: number): string {
    return String.fromCharCode(65 + index);
  }

  toggleDetailedResults(): void {
    this.showDetailedResults = !this.showDetailedResults;
  }

  toggleQuestionDetails(index: number): void {
    this.expandedQuestionIndex = this.expandedQuestionIndex === index ? null : index;
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  /**
   * Le rapport n'a de sens que derrière un test du référentiel : c'est lui qui
   * évalue des compétences. Un test composé depuis le vivier de questions
   * produit un score, pas un bilan d'acquisition.
   *
   * Attention au nommage des modes, contre-intuitif mais établi : en base, le
   * test diagnostique porte `evaluation` et le devoir de contrôle `exam`.
   */
  get canOpenReport(): boolean {
    const mode = this.testResult?.test?.testMode;
    return mode === 'evaluation' || mode === 'exam';
  }

  /**
   * Affiche le rapport de diagnostic, restreint à la matière du test.
   *
   * Remplace l'ancien PDF par session généré côté serveur : celui-ci ne
   * rendait que le détail des réponses, alors que le rapport livré porte les
   * paliers d'évaluation, les compétences acquises ou non et le plan de
   * remédiation. Deux documents pour la même intention divergeaient à chaque
   * évolution du référentiel.
   *
   * Le rapport est un écran de l'application depuis qu'il n'est plus un
   * document statique : une navigation, donc, et non plus un onglet ouvert à
   * qui l'on passait un jeton. L'élève y arrive sur sa matière ; la barre du
   * rapport le mène ensuite aux autres et au bilan d'ensemble.
   */
  openDiagnosticReport(): void {
    const subjectId = this.testResult?.test?.subjectId ?? null;

    void this.router.navigate(['/diagnostic-report'], {
      queryParams: subjectId ? { subject: subjectId } : {},
    });
  }

  goBackToTests(): void {
    this.router.navigate(['/student/tests']);
  }

  retakeTest(): void {
    if (!this.testId || !this.canRetake) return;
    this.router.navigate(['/student/test/take', this.testId]);
  }

  /**
   * Demande au serveur si ce test reste ouvert à cet élève.
   *
   * <p>La liste des tests disponibles applique déjà la règle complète —
   * fenêtre de passation, tentatives restantes, statut du test. S'y fier évite
   * d'en tenir une seconde version ici, et garantit que le bouton n'ouvre
   * jamais une session que le serveur refusera.</p>
   */
  private checkRetakeAllowed(test: Test): void {
    this.reprise = 'inconnu';
    this.tentativesRestantes = null;

    if (!test?.subjectId || !test?.testMode || !this.testId) return;

    // Silencieuse : la copie est déjà affichée, et savoir si le test se repasse
    // est une information secondaire. Son échec ne doit pas recouvrir la note
    // d'une fenêtre d'erreur — l'écran se replie sur la formulation neutre.
    this.studentTestService
      .getAvailableByModeAndSubject(test.testMode, test.subjectId, { silencieuse: true })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const { etat, restantes } = etatReprise(res?.data, this.testId);
          this.reprise = etat;
          this.tentativesRestantes = restantes;
          this.cdr.detectChanges();
        },
        // Sur échec, ni bouton ni promesse : un bouton proposé à tort renvoie
        // l'élève sur une erreur, et un « vous pouvez recommencer » non vérifié
        // est précisément le défaut que l'état « inconnu » évite.
        error: () => {
          this.reprise = 'inconnu';
          this.tentativesRestantes = null;
          this.cdr.detectChanges();
        },
      });
  }

  /**
   * Temps réellement passé sur la copie, en clair et avec ses unités.
   *
   * <p>Rendait auparavant un `M:SS` sans unité — « 50:12 » se lisait comme
   * cinquante de quelque chose, et se confondait avec la durée allouée au
   * test, qui est une tout autre grandeur. Un tiret quand le serveur n'a rien
   * chronométré : mieux vaut l'absence qu'un zéro qui affirme.</p>
   */
  get timeSpentLabel(): string {
    const seconds = this.testResult?.timeSpent;
    if (seconds == null || seconds <= 0) return '—';
    return this.durationPipe.transform(seconds);
  }

  /** Durée allouée au test, quand il en a une. `0` vaut « sans limite ». */
  get timeLimitLabel(): string | null {
    const minutes = this.testResult?.test?.timeLimitMinutes;
    if (!minutes || minutes <= 0) return null;
    return `${minutes} ${this.translate.instant('TIME.MIN')}`;
  }

  /**
   * Message d'échec présenté à l'élève.
   *
   * <p>Le message du serveur d'abord, la phrase traduite ensuite. Ce qui
   * remontait auparavant, c'était le `message` d'une `Error` du code : l'élève
   * lisait une phrase de développeur, en anglais, qui lui demandait de repasser
   * un test déjà rendu.</p>
   */
  private handleError(error: any): void {
    this.isLoading = false;
    this.hasError = true;
    this.errorMessage =
      error?.error?.message || this.translate.instant('ERRORS.LOAD_RESULTS_ERROR');
    this.swal.error(this.errorMessage);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
